import React, { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { WhatsappTemplate } from '@lgg/isomorphic/types/__generated__/graphql';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { AddWhatsappTemplateOption } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp-template/add-whatsapp-template-option';
import { ContactInteractionsReplyModeInfoMessage } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interactions-reply-mode-message';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useFormatDate } from 'src/hooks/use-format-date';

const NotificationTemplateButtonContainer = styled(FlexRow)`
  align-items: center;
  cursor: pointer;
  margin: 0;

  ${up('md')} {
    margin-top: 10px;
  }
`;

const NotificationTemplateOptionIcon = styled(Icon)`
  margin-right: 4px;

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${({ theme }) => theme.colors.secondaryTopaz};
    }
  }
`;

const NotificationTemplateOptionText = styled.span`
  color: ${({ theme }) => theme.colors.secondaryTopaz};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 14px;
  margin: 0;
`;

const WhatsappMessageSubtitle = styled.span`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 18px;
  margin: 0;
  white-space: break-spaces;
`;

const BodyStrongText = styled.span`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 15px;
`;

const ViewDetailsLink = styled.a`
  color: ${({ theme }) => theme.colors.gogo};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 15px;
  margin-top: 10px;
`;

export const NotificationTemplateButton = ({ onClick }: { onClick: VoidFunction }) => {
  const breakpointUpMd = useBreakpoint(up('md'));

  return (
    <NotificationTemplateButtonContainer
      onClick={onClick}
      data-lgg-id="whatsapp-message-window-closed-add-template-option"
    >
      {breakpointUpMd ? (
        <>
          <NotificationTemplateOptionIcon type="whatsappTemplates" />
          <NotificationTemplateOptionText>
            <Trans i18nKey="conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.selectMessageTemplate.desktop" />
          </NotificationTemplateOptionText>
        </>
      ) : (
        <NotificationTemplateOptionText>
          <Trans i18nKey="conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.selectMessageTemplate.mobile" />
        </NotificationTemplateOptionText>
      )}
    </NotificationTemplateButtonContainer>
  );
};

export type WhatsappMessageClosedWindowContentProps = {
  isWindowOpen: boolean;
  resourceId: string;
  onTemplateSelect: (template: WhatsappTemplate) => void;
};

export const WhatsappMessageClosedWindowContent = ({
  isWindowOpen,
  resourceId,
  onTemplateSelect,
}: WhatsappMessageClosedWindowContentProps) => {
  const { t } = useTranslation(['conversations']);
  const breakpointUpMd = useBreakpoint(up('md'));

  if (isWindowOpen) return null;

  return (
    <ContactInteractionsReplyModeInfoMessage
      lggId="whatsapp-message-window-closed-info-message"
      title={t(
        'conversations:messageInput.modes.reply.messages.whatsapp.messageWindowClosed.title',
      )}
      body={
        <>
          {breakpointUpMd && (
            <WhatsappMessageSubtitle>
              {t(
                'conversations:messageInput.modes.reply.messages.whatsapp.messageWindowClosed.body',
              )}
            </WhatsappMessageSubtitle>
          )}
          <AddWhatsappTemplateOption
            resourceData={{
              resourceId,
            }}
            renderTrigger={({ showModal }) => (
              <NotificationTemplateButton onClick={showModal} />
            )}
            onTemplateSelect={onTemplateSelect}
          />
        </>
      }
    />
  );
};

type WhatsappBannedResourceBannerProps = {
  violation?: string;
  date?: Date;
  waba: string;
};

export const WhatsappBannedResourceBanner = memo<WhatsappBannedResourceBannerProps>(
  ({ violation, date, waba }) => {
    const { t } = useTranslation(['conversations']);
    const { formatSimpleDate } = useFormatDate();

    if (!violation || !date) {
      return null;
    }

    return (
      <ContactInteractionsReplyModeInfoMessage
        lggId="whatsapp-resource-banned-banner-info-message"
        title={t(
          'conversations:messageInput.modes.reply.messages.whatsapp.bannedResource.title',
        )}
        body={
          <FlexColumn>
            <WhatsappMessageSubtitle>
              <Trans
                i18nKey="conversations:messageInput.modes.reply.messages.whatsapp.bannedResource.body"
                values={{ violation, date: formatSimpleDate(date) }}
                components={{ strong: <BodyStrongText /> }}
              />
            </WhatsappMessageSubtitle>
            <ViewDetailsLink
              href={`https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${waba}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(
                'conversations:messageInput.modes.reply.messages.whatsapp.bannedResource.actionLink',
              )}
            </ViewDetailsLink>
          </FlexColumn>
        }
      />
    );
  },
);

export const WhatsappResourceReviewPendingBanner = memo(() => {
  const { t } = useTranslation(['conversations']);

  return (
    <ContactInteractionsReplyModeInfoMessage
      lggId="whatsapp-resource-review-pending-banner-info-message"
      title={t(
        'conversations:messageInput.modes.reply.messages.whatsapp.reviewPendingResource.title',
      )}
      body={t(
        'conversations:messageInput.modes.reply.messages.whatsapp.reviewPendingResource.body',
      )}
    />
  );
});

type WhatsappResourceInactiveBannerProps = {
  waba: string;
};

export const WhatsappResourceInactiveBanner = memo(
  ({ waba }: WhatsappResourceInactiveBannerProps) => {
    const { t } = useTranslation(['conversations']);
    const { hasPermission } = useAuthorization();
    const isManager = hasPermission('platform:company:update');

    return (
      <ContactInteractionsReplyModeInfoMessage
        lggId="whatsapp-resource-inactive-info-message"
        title={t(
          'conversations:messageInput.modes.reply.messages.whatsapp.inactiveResource.title',
        )}
        color="secondaryGold"
        body={
          isManager ? (
            <FlexColumn>
              <WhatsappMessageSubtitle>
                {t(
                  'conversations:messageInput.modes.reply.messages.whatsapp.inactiveResource.body.manager',
                )}
              </WhatsappMessageSubtitle>
              <ViewDetailsLink
                href={`https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${waba}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(
                  'conversations:messageInput.modes.reply.messages.whatsapp.inactiveResource.actionLink',
                )}
              </ViewDetailsLink>
            </FlexColumn>
          ) : (
            t(
              'conversations:messageInput.modes.reply.messages.whatsapp.inactiveResource.body.agent',
            )
          )
        }
      />
    );
  },
);
